import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat';
import { useDispatch } from 'react-redux';
import { VaMemorableDate } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { recordEventOnce } from 'platform/monitoring/record-event';
import Wrapper from '../layout/Wrapper';
import { usePostAuthenticationMutation } from '../redux/api/vassApi';
import { clearFormData, setFlowType } from '../redux/slices/formSlice';
import {
  ANALYTICS_EVENTS,
  FLOW_TYPES,
  SOLID_START_LANDING_URL,
  URLS,
  VASS_PHONE_NUMBER,
} from '../utils/constants';
import {
  buildInvitationSearch,
  hasCompleteInvitation,
} from '../utils/invitationQuery';
import { useErrorFocus } from '../hooks/useErrorFocus';
import {
  isInvalidCredentialsError,
  isRateLimitExceededError,
  isServerError,
  isMissingContactInformationError,
  isNotificationError,
} from '../utils/errors';

const getPageTitle = (cancellationFlow, verificationError) => {
  if (verificationError) {
    return 'We couldn’t verify your information';
  }
  if (cancellationFlow) {
    return 'Cancel VA Solid Start call';
  }
  return 'Schedule a call with VA Solid Start';
};

const Verify = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Check for cancel=true URL parameter to initiate cancellation flow
  const cancellationFlow = searchParams.get('cancel') === 'true';
  const uuid = searchParams.get('uuid');
  const veteranContactEmail = searchParams.get('veteranContactEmail');
  const invitationData = searchParams.get('data');
  const completeInvitation = hasCompleteInvitation({
    uuid,
    veteranContactEmail,
    data: invitationData,
  });

  // If the user is hitting the bare URL, then this is not an invitation and we should forward them immediately
  useEffect(() => {
    if (!completeInvitation) {
      window.location.replace(SOLID_START_LANDING_URL); // This is a Drupal page
    }
  }, [completeInvitation]);

  // Ensures a fresh start when landing on Verify page and sets the flow type
  useEffect(() => {
    dispatch(clearFormData());
    // Set flow type based on URL parameter
    const flowType = cancellationFlow ? FLOW_TYPES.CANCEL : FLOW_TYPES.SCHEDULE;
    dispatch(setFlowType(flowType));

    // Track invitation email click-throughs for VASS metrics reporting.
    // The invitation email is the only entry point that supplies uuid plus
    // veteranContactEmail or data, so their presence marks a click-through.
    // recordEventOnce dedupes against the dataLayer so internal redirects
    // back to Verify (which rebuild the same params) don't inflate the count.
    // Note: no PII (uuid/email) is sent to GA — only the flow type.
    if (uuid && (veteranContactEmail || invitationData)) {
      recordEventOnce(
        {
          event: ANALYTICS_EVENTS.EMAIL_CLICK_THROUGH,
          'vass-flow-type': flowType,
        },
        'event',
      );
    }
  }, [dispatch, cancellationFlow, uuid, veteranContactEmail, invitationData]);

  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');

  const [postAuthentication, { isLoading, error: postAuthenticationError }] =
    usePostAuthenticationMutation();

  const [
    { error: lastNameError, handleSetError: setLastNameError },
    { error: dobError, handleSetError: setDobError },
    { handleSetError: setAuthError },
  ] = useErrorFocus([
    'va-text-input[data-testid="last-name-input"]',
    'va-memorable-date[data-testid="dob-input"]',
    'va-alert[data-testid="verify-error-alert"]',
  ]);

  const [attemptCount, setAttemptCount] = useState(1);
  const [verificationError, setVerificationError] = useState(undefined);

  const handleSubmit = async () => {
    if (lastName === '' || dob === '') {
      if (lastName === '') {
        setLastNameError('Please enter your last name');
      }
      if (dob === '') {
        setDobError('Please enter your date of birth');
      }
      return;
    }
    const response = await postAuthentication({
      uuid,
      lastName,
      dob,
      veteranContactEmail,
      invitationData,
    });
    if (response.error) {
      setAuthError('error');
      if (attemptCount === 3 || isRateLimitExceededError(response.error)) {
        setVerificationError(
          'We’re sorry. We couldn’t match your information to your records. Call us for help.',
        );
      }
      setAttemptCount(count => count + 1);
      return;
    }
    navigate(
      `${URLS.ENTER_OTP}${buildInvitationSearch({
        uuid,
        veteranContactEmail,
        data: invitationData,
        cancel: cancellationFlow,
      })}`,
    );
  };

  const pageTitle = getPageTitle(cancellationFlow, verificationError);
  const dirty = lastName !== '' || dob !== '';

  // Render nothing while the redirect effect above navigates away, so the
  // Verify form never flashes for visitors without a complete invitation.
  if (!completeInvitation) {
    return null;
  }

  return (
    <Wrapper
      errorAlert={
        isServerError(postAuthenticationError) ||
        isMissingContactInformationError(postAuthenticationError) ||
        isNotificationError(postAuthenticationError)
      }
      pageTitle={pageTitle}
      verificationError={verificationError}
      disableBeforeUnload={!dirty}
      flowType={cancellationFlow ? FLOW_TYPES.CANCEL : FLOW_TYPES.SCHEDULE}
    >
      <p data-testid="verify-intro-text">
        First, we’ll need your information so we can send you a one-time
        passcode to verify your identity.
      </p>
      {isInvalidCredentialsError(postAuthenticationError) && (
        <div className="vads-u-margin-bottom--2">
          <va-alert data-testid="verify-error-alert" status="error">
            We’re sorry. We couldn’t find a record that matches that last name
            or date of birth. Please try again or call us at{' '}
            <va-telephone contact={VASS_PHONE_NUMBER} /> for scheduling
            assistance.
          </va-alert>
        </div>
      )}
      <va-text-input
        data-dd-privacy="mask"
        data-testid="last-name-input"
        label="Your last name"
        value={lastName}
        name="last-name"
        onBlur={e => {
          // Clear the error if the user has entered a value
          if (e.target.value !== '') {
            setLastNameError('');
          }
        }}
        onInput={e => {
          setLastName(e.target.value);
        }}
        required
        error={lastNameError}
        show-input-error
      />
      <VaMemorableDate
        data-dd-privacy="mask"
        id="dob-input"
        data-testid="dob-input"
        label="Date of birth"
        value={dob}
        onDateBlur={e => {
          // Clear the error if the user has entered a value
          if (e.target.value !== '') {
            setDobError('');
          }
        }}
        onDateChange={e => {
          setDob(e.target.value);
        }}
        name="date-of-birth"
        error={dobError}
        required
        monthSelect={false}
      />
      <div className="vads-u-display--flex vads-u-margin-top--4 vass-form__button-container vass-flex-direction--column">
        <va-button
          data-testid="submit-button"
          big
          disabled={isLoading || verificationError}
          onClick={handleSubmit}
          text="Submit"
          uswds
          loading={isLoading}
        />
      </div>
    </Wrapper>
  );
};

export default Verify;
